import { TFunction } from 'i18next';
import { append, isNil } from 'ramda';
import { filterUndefinedRows } from 'src/js/pages/statistic/statistic-mapper';
import { StatisticSectionViewModel } from 'src/js/pages/statistic/statistic-view-model';
import { toTitleCase } from 'src/js/util/global-util';

import { formatUptime, translateStreamEncapsulation, translateStreamState } from './stream-helper';
import {
  formatHourMinute,
  RecordLimitType,
  SrtPathRedundancyMode,
  StreamEncapsulation,
  StreamViewModel,
} from './stream-models';
import { numberWithCommas } from 'src/js/util/string-util';
import { formatBytesWithUnits } from './stream-view-model-mapper';
import { storageStore } from '../recordings/storage-store';

export const mapStreamToStatistics = (
  stream: StreamViewModel,
  fullPage: boolean,
  t: TFunction,
): StatisticSectionViewModel[] => {
  const isSRT = !isNil(stream.stats.srt);
  const isWHIP = stream.encapsulation === StreamEncapsulation.WHIP;

  let stats: StatisticSectionViewModel[] = [
    {
      title: t('statistics.header.statistics'),
      toggleGroup: isSRT ? 'stream-recordings' : undefined,
      toggleLabel: t('statistics.header.streamToggleLabel'),
      span: 2,
      rows: [
        {
          name: t('statistics.stream.rows.id'),
          value: stream.id.toString(),
        },
        /* If we ever want to display the name in a full page statistics {
          name: t('statistics.stream.rows.name'),
          value: streamListStreamName(t, stream.name),
        },*/
        {
          name: t('general.status'),
          value: toTitleCase(translateStreamState(stream.stats.state, t), false),
          shortlisted: true,
        },
        {
          name: t('statistics.stream.rows.encapsulation'),
          value: translateStreamEncapsulation(stream.encapsulation, t),
        },
        {
          name: t('statistics.stream.rows.uptime'),
          value: formatUptime(stream.stats.uptime),
          shortlisted: true,
        },
        {
          name: t('statistics.stream.rows.destAddress'),
          value: stream.stats.destAddress,
        },
        // FIXME This doesn't make any sense
        {
          name: t('statistics.stream.rows.sourcePort'),
          value: stream.stats.srt ? stream.stats.sourcePort : undefined,
          shortlisted: true,
          when: !isSRT,
        },
        {
          name: t('statistics.stream.rows.sentPackets'),
          value: stream.stats.sentPackets,
          shortlisted: true,
        },
        {
          name: t('statistics.stream.rows.sentBytes'),
          value: stream.stats.sentBytes,
          shortlisted: true,
          when: (!fullPage && !isSRT) || fullPage,
        },
        {
          name: t('statistics.stream.rows.unsentPackets'),
          value: stream.stats.unsentPackets,
          shortlisted: true,
          when: (!fullPage && isSRT) || fullPage,
        },
        {
          name: t('statistics.stream.rows.unsentBytes'),
          value: stream.stats.unsentBytes,
          shortlisted: true,
          when: !isWHIP && ((!fullPage && !isSRT) || fullPage),
        },
        {
          name: t('statistics.stream.rows.droppedPackets'),
          value: stream.stats.srt?.droppedPackets,
          shortlisted: true,
          when: !fullPage && isSRT,
        },
        {
          name: t('statistics.stream.rows.lostPackets'),
          value: stream.stats.srt?.lostPackets,
          shortlisted: true,
          when: !fullPage && isSRT,
        },
        {
          name: t('statistics.stream.rows.rtt'),
          value: stream.stats.srt?.rrtWithUnits,
          shortlisted: true,
          when: !fullPage && isSRT,
        },
        {
          name: t('statistics.stream.rows.latency'),
          value: stream.stats.srt?.latency.toString() + ' ms',
          shortlisted: true,
          when: !fullPage && isSRT,
        },
        {
          name: t('statistics.stream.rows.rtcp'),
          value: stream.stats.rtcp,
        },
        {
          name: t('statistics.stream.rows.rxOKRTCPPackets'),
          value: stream.stats.rxOKRTCPPackets,
        },
        {
          name: t('statistics.stream.rows.rxOKRTCPBytes'),
          value: stream.stats.rxOKRTCPBytes,
        },
        {
          name: t('statistics.stream.rows.rxInvalidRTCPPackets'),
          value: stream.stats.rxInvalidRTCPPackets,
        },
        {
          name: t('statistics.stream.rows.rxInvalidRTCPBytes'),
          value: stream.stats.rxInvalidRTCPBytes,
        },
        {
          name: t('statistics.stream.rows.txOKRTCPBytes'),
          value: stream.stats.txOKRTCPBytes,
        },
        {
          name: t('statistics.stream.rows.txFailedRTCPPackets'),
          value: stream.stats.txFailedRTCPPackets,
        },
        {
          name: t('statistics.stream.rows.RTCPReporters'),
          value: stream.stats.RTCPReporters,
        },
        {
          name: t('statistics.stream.rows.lostPackets'),
          value: stream.stats.lostPackets,
        },
        {
          name: t('statistics.stream.rows.duplicate'),
          value: stream.stats.duplicate,
        },
        {
          name: t('statistics.stream.rows.bitrate'),
          value: [numberWithCommas(stream.stats.bitrateInKbps), 'kbps'],
          formatString: true,
          shortlisted: true,
        },
        {
          name: t('statistics.stream.rows.whipLocalPort'),
          value: stream.stats.whipLocalPort,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipRemotePort'),
          value: stream.stats.whipRemotePort,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipFullIntraRequests'),
          value: stream.stats.whipFullIntraRequests,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipPictureLoss'),
          value: stream.stats.whipPictureLoss,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipNACK'),
          value: stream.stats.whipNACK,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipPacketLoss'),
          value: stream.stats.whipPacketLoss,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipRTT'),
          value: stream.stats.whipRTT,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipJitter'),
          value: stream.stats.whipJitter,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipLastError'),
          value:
            stream.stats.whipLastError && stream.stats.whipLastError !== '' ? stream.stats.whipLastError : undefined,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.whipLastErrorOccured'),
          value:
            stream.stats.whipLastErrorOccured && stream.stats.whipLastErrorOccured !== ''
              ? stream.stats.whipLastErrorOccured
              : undefined,
          shortlisted: true,
          when: isWHIP,
        },
        {
          name: t('statistics.stream.rows.detectedRedundancyMode'),
          value:
            stream.stats.srt?.detectedRedundancyMode === SrtPathRedundancyMode.NONE ||
            stream.stats.srt?.detectedRedundancyMode === undefined
              ? undefined
              : t(`stream.srtRedundancyOptions.${stream.stats.srt?.detectedRedundancyMode}`),
          shortlisted: true,
        },
        {
          name: t('statistics.stream.rows.sourceAddress'),
          value: stream.stats.sourceAddress,
        },
        {
          name: t('statistics.stream.rows.connections'),
          value: stream.stats.connections,
        },
        {
          name: t('statistics.stream.rows.programNumber'),
          value: stream.stats.programNumber,
        },
        {
          name: t('statistics.stream.rows.pcrPid'),
          value: stream.stats.pcrPid,
        },
        {
          name: t('statistics.stream.rows.lastReset'),
          value: formatUptime(stream.stats.lastReset),
          shortlisted: true,
        },
      ],
    },
  ];

  if (stream.encapsulation === StreamEncapsulation.SRT && isSRT) {
    stats = append(
      {
        title: `${t('statistics.stream.section.srt')} (${t('statistics.stream.srtVersionPrefix')} ${
          stream.stats.srt.localVersion
        })`,
        twoColumns: stream.stats.srt.path2 !== undefined,
        className: 'srt',
        span: 3,
        rows: [
          {
            name: t('statistics.stream.rows.path'),
            value: toTitleCase(stream.stats.srt.pathName),
            value2: toTitleCase(stream.stats.srt.path2?.pathName),
            className: 'light',
            when: !isNil(stream.stats.srt.path2),
          },
          {
            name: t('general.status'),
            value: toTitleCase(translateStreamState(stream.stats.srt.state, t), false),
            value2: toTitleCase(translateStreamState(stream.stats.srt.path2?.state, t), false),
          },
          {
            name: t('statistics.stream.rows.peerVersion'),
            value: stream.stats.srt.peerVersion,
            value2: stream.stats.srt.path2?.peerVersion,
          },
          {
            name: t('statistics.stream.rows.peerEndpoint'),
            value: stream.stats.srt.peerEndpoint,
            value2: stream.stats.srt.path2?.peerEndpoint,
          },
          {
            name: t('statistics.stream.rows.localEndpoint'),
            value: stream.stats.srt.localEndpoint,
            value2: stream.stats.srt.path2?.localEndpoint,
          },
          {
            name: t('statistics.stream.rows.reconnections'),
            value: stream.stats.srt.reconnections,
            value2: stream.stats.srt.path2?.reconnections,
          },
          {
            name: t('statistics.stream.rows.rejectedLastTime'),
            value: stream.stats.srt.rejectedLastTime,
          },
          {
            name: t('statistics.stream.rows.rejectedReason'),
            value: stream.stats.srt.rejectedReason,
          },
          {
            name: t('statistics.stream.rows.rejectedStreamId'),
            value: stream.stats.srt.rejectedStreamId,
          },
          {
            name: t('statistics.stream.rows.localPort'),
            value: stream.stats.srt.localPort,
            value2: stream.stats.srt.path2?.localPort,
          },
          {
            name: t('statistics.stream.rows.encryptionMode'),
            value: stream.stats.srt.encryptionMode,
            value2: stream.stats.srt.path2?.encryptionMode,
          },
          {
            name: t('statistics.stream.rows.encryption'),
            value: stream.encKeyLength ? t('general.on') : t('general.off'),
            value2:
              stream.stats.srt?.path2 === undefined
                ? undefined
                : stream.encKeyLength
                ? t('general.on')
                : t('general.off'),
            when: stream.stats.srt.encryptionMode === '',
          },
          {
            name: t('statistics.stream.rows.keyLength'),
            value: stream.stats.srt.keyLength,
            value2: stream.stats.srt.path2?.keyLength,
          },
          {
            name: t('statistics.stream.rows.authentication'),
            value: stream.stats.srt.authentication,
            value2: stream.stats.srt.path2?.authentication,
          },
          {
            name: t('statistics.stream.rows.peerDecryption'),
            value: stream.stats.srt.peerDecryption,
            value2: stream.stats.srt.path2?.peerDecryption,
            when: stream.stats.srt.encryption === 'On',
          },
          {
            name: t('statistics.stream.rows.resentPackets'),
            value: stream.stats.srt.resentPackets,
            value2: stream.stats.srt.path2?.resentPackets,
          },
          {
            name: t('statistics.stream.rows.resentBytes'),
            value: stream.stats.srt.resentBytes,
            value2: stream.stats.srt.path2?.resentBytes,
          },
          {
            name: t('statistics.stream.rows.lostPackets'),
            value: stream.stats.srt.lostPackets,
            value2: stream.stats.srt.path2?.lostPackets,
          },
          {
            name: t('statistics.stream.rows.droppedPackets'),
            value: stream.stats.srt.droppedPackets,
            value2: stream.stats.srt.path2?.droppedPackets,
          },
          {
            name: t('statistics.stream.rows.droppedBytes'),
            value: stream.stats.srt.droppedBytes,
            value2: stream.stats.srt.path2?.droppedBytes,
          },
          {
            name: t('statistics.stream.rows.recvACK'),
            value: stream.stats.srt.recvACK,
            value2: stream.stats.srt.path2?.recvACK,
          },
          {
            name: t('statistics.stream.rows.recvNAK'),
            value: stream.stats.srt.recvNAK,
            value2: stream.stats.srt.path2?.recvNAK,
          },
          {
            name: t('statistics.stream.rows.maxBandwidth'),
            value: stream.stats.srt.maxBandwidth,
            value2: stream.stats.srt.path2?.maxBandwidth,
          },
          {
            name: t('statistics.stream.rows.pathMaxBandwidth'),
            value: stream.stats.srt.pathMaxBandwidth,
            value2: stream.stats.srt.path2?.pathMaxBandwidth,
          },
          {
            name: t('statistics.stream.rows.mss'),
            value: stream.stats.srt.mss,
            value2: stream.stats.srt.path2?.mss,
          },
          {
            name: t('statistics.stream.rows.rtt'),
            value: stream.stats.srt.rrtWithUnits,
            value2: stream.stats.srt.path2?.rrtWithUnits,
          },
          {
            name: t('statistics.stream.rows.buffer'),
            value: stream.stats.srt.buffer.toString() + ' ms',
            value2: stream.stats.srt.path2?.buffer.toString() + ' ms', //FixME test
          },
          {
            name: t('statistics.stream.rows.latency'),
            value: stream.stats.srt.latency.toString() + ' ms',
            value2: stream.stats.srt.path2?.latency.toString() + ' ms',
          },
        ],
      },
      stats,
    );
  }

  stats = append(
    {
      title: t('statistics.stream.section.errors'),
      rows: [
        {
          name: t('statistics.stream.rows.lastError'),
          value: stream.stats.lastError,
        },
        {
          name: t('statistics.stream.rows.occurred'),
          value: stream.stats.occurred,
        },
        {
          name: t('statistics.stream.rows.lastRxError') /* RTCP */,
          value: stream.stats.lastRxError,
        },
        {
          name: t('statistics.stream.rows.lastTxError'),
          value: stream.stats.lastTxError,
        },
        {
          name: t('statistics.stream.rows.rtmpLastError'),
          value: stream.stats.rtmpLastError?.toString(),
        },
        {
          name: t('statistics.stream.rows.corruptedFrames'),
          value: stream.stats.corruptedFrames?.toString(),
        },
        {
          name: t('statistics.stream.rows.droppedPackets'),
          value: stream.stats.droppedPackets,
        },
        {
          name: t('statistics.stream.rows.restarts'),
          value: stream.stats.restarts?.toString(),
        },
      ],
    },
    stats,
  );

  if (stream.stats.sources) {
    for (let i = 0; i < stream.stats.sources.length; i++) {
      const pSource = stream.stats.sources[i];
      stats = append(
        {
          title: pSource.name,
          rows: [
            {
              name: t('statistics.stream.rows.sourceCompression'),
              value: pSource.compression,
            },
            {
              name: t('statistics.stream.rows.sourceBitrate'),
              value: pSource.bitrate,
            },
            {
              name: t('statistics.stream.rows.sourceProgramId'),
              value: pSource.programId?.toString(),
            },
            {
              name: t('statistics.stream.rows.sourceReceivedPackets'),
              value: pSource.receivedPackets,
            },
            {
              name: t('statistics.stream.rows.sourceReceivedBytes'),
              value: pSource.receivedBytes,
            },
            {
              name: t('statistics.stream.rows.sourcePts'),
              value: pSource.PTS,
            },
            {
              name: t('statistics.stream.rows.sourceDts'),
              value: pSource.DTS,
            },
          ],
        },
        stats,
      );
    }
  }

  if (stream.recording?.enabled === true) {
    const recording = stream.recording;

    const bytesStoredFormatted = formatBytesWithUnits(recording.bytesStored, 2, true);
    let totalLimitHumanFormatted;
    if (recording.limitType === RecordLimitType.TIME) {
      //if (recording.upTime) {
      // fixme remove 0s
      totalLimitHumanFormatted = formatHourMinute(
        recording.limitSizeHour?.toString(),
        recording.limitSizeMinutes?.toString(),
      );
      if (totalLimitHumanFormatted.endsWith('h0m')) {
        totalLimitHumanFormatted = totalLimitHumanFormatted.substring(0, totalLimitHumanFormatted.length - 2); // remove trailing "0m" in for example "1h0m"
      }
      if (totalLimitHumanFormatted.startsWith('0h') && totalLimitHumanFormatted.length > 3) {
        totalLimitHumanFormatted = totalLimitHumanFormatted.substring(2, totalLimitHumanFormatted.length); // remove leading "0h" in for example "0h50m"
      }
      // }
    } else if (recording.limitType === RecordLimitType.SIZE) {
      const minimalistic = true;
      const bytesFormatted = formatBytesWithUnits(Number(recording.limitSizeFromApi) * 1024, 2, minimalistic);
      // const bytesFormatted2 = `${recording.limitSize} ${recording.limitSizeUnit}`;
      totalLimitHumanFormatted = bytesFormatted.string;
    }

    stats = append(
      {
        title: t('statistics.header.recording'),
        toggleGroup: !fullPage && 'stream-recordings',
        toggleLabel: t('statistics.header.recordingToggleLabel'),
        span: 2,
        rows: [
          {
            name: t('statistics.stream.rows.recording.state'),
            value: toTitleCase(recording.status),
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.upTime'),
            value: recording.upTime?.length > 0 ? formatUptime(recording.upTime) : undefined,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.name'),
            value: recording.name,
            shortlisted: true,
            when: false, // not in UX
          },
          {
            name: t('statistics.stream.rows.recording.filename'),
            value: recording.filename?.startsWith(storageStore.internal?.path)
              ? recording.filename.substring(storageStore.internal?.path.length + 1)
              : recording.filename,
            copyPaste: true,
            copyPasteContent: recording.filename,
            shortlisted: true,
            when: recording.filename != null && recording.filename !== '',
          },
          {
            name: t('statistics.stream.rows.recording.limit'),
            value: totalLimitHumanFormatted,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.segmented'),
            value: recording.segmented ? t('general.on') : t('general.off'),
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.autoStart'),
            value: recording.autoStart ? t('general.on') : t('general.off'),
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.percentageDone'),
            value: recording.percentageDone ? `${recording.percentageDone}%` : undefined,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.storedBytes'),
            value: recording.bytesStored ? [bytesStoredFormatted.size, bytesStoredFormatted.unit] : undefined,
            formatString: true,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.queueDepth'),
            value: recording.queueDepth,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.peakQueueDepth'),
            value: recording.peakQueueDepth,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.queueOverflows'),
            value: recording.queueOverflows,
            shortlisted: true,
          },
          {
            name: t('statistics.stream.rows.recording.lastQueueOverflow'),
            value: formatUptime(recording.lastQueueOverflow),
            when: Number(recording?.queueOverflows) > 0,
            shortlisted: true,
          },
        ],
      },
      stats,
    );
  }

  // remove undefined rows
  return filterUndefinedRows(stats);
};
